import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	VStack,
	FormControl,
	FormLabel,
	Input,
	Select,
	Textarea,
	Switch,
	Button,
	Divider,
	Heading,
	HStack,
	Box,
	useToast,
	Spinner,
	Center,
	Image,
	Text,
} from '@chakra-ui/react';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import axiosInstance from '../../../Api/axiosInstance';
import { getDefaultWorkspaceBranding } from '../../../constants/defaultWorkspaceBranding';
import { UploadthingWidget } from '../../../utils/uploadthing/UploadWidget';
import {
	invalidateCompanyBrandingCache,
	patchCompanyBrandingLogoInCache,
} from '../../../hooks/useCompany';

const SettingsModal = ({ isOpen, onClose }) => {
	const { companyId } = useAuthenticatedUserInfo();
	const queryClient = useQueryClient();
	const toast = useToast();
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [settings, setSettings] = useState({
		industry: 'technology',
		timezone: 'utc-5',
		emailSignature: '',
		workingHours: {
			start: '09:00',
			end: '17:00',
		},
		notifications: {
			emailCampaigns: true,
			systemAlerts: true,
			teamActivity: false,
		},
		branding: getDefaultWorkspaceBranding(),
	});

	// Fetch company settings on modal open
	useEffect(() => {
		if (isOpen && companyId) {
			fetchCompanySettings();
		}
	}, [isOpen, companyId]);

	const fetchCompanySettings = async () => {
		setIsLoading(true);
		try {
			const response = await axiosInstance.get(
				`/companies/${companyId}/settings`,
			);
			if (response.data.settings) {
				setSettings(response.data.settings);
			}
		} catch (error) {
			console.error('Error fetching company settings:', error);
			toast({
				title: 'Error',
				description: 'Failed to load company settings',
				status: 'error',
				duration: 3000,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSaveSettings = async () => {
		setIsSaving(true);
		try {
			await axiosInstance.put(`/companies/${companyId}/settings`, {
				settings,
			});
			patchCompanyBrandingLogoInCache(
				queryClient,
				companyId,
				settings?.branding?.logo || '',
			);
			invalidateCompanyBrandingCache(queryClient, companyId);
			toast({
				title: 'Success',
				description: 'Company settings updated successfully',
				status: 'success',
				duration: 3000,
			});
			onClose();
		} catch (error) {
			console.error('Error saving company settings:', error);
			toast({
				title: 'Error',
				description: 'Failed to save company settings',
				status: 'error',
				duration: 3000,
			});
		} finally {
			setIsSaving(false);
		}
	};

	const updateSetting = (path, value) => {
		setSettings((prev) => {
			const newSettings = { ...prev };
			const keys = path.split('.');
			let current = newSettings;

			for (let i = 0; i < keys.length - 1; i++) {
				if (!current[keys[i]]) current[keys[i]] = {};
				current = current[keys[i]];
			}
			current[keys[keys.length - 1]] = value;
			return newSettings;
		});
	};

	if (isLoading) {
		return (
			<Modal isOpen={isOpen} onClose={onClose} size='lg'>
				<ModalOverlay />
				<ModalContent>
					<ModalBody>
						<Center h='200px'>
							<VStack spacing={4}>
								<Spinner size='lg' color='blue.500' />
								<Box>Loading company settings...</Box>
							</VStack>
						</Center>
					</ModalBody>
				</ModalContent>
			</Modal>
		);
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} size='4xl'>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Company Settings</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={6}>
					<VStack spacing={6} align='stretch'>
						{/* General Settings */}
						<Box>
							<Heading size='md' mb={4}>
								General Settings
							</Heading>
							<VStack spacing={4} align='stretch'>
								<FormControl>
									<FormLabel>Industry</FormLabel>
									<Select
										value={settings.industry}
										onChange={(e) => updateSetting('industry', e.target.value)}>
										<option value='technology'>Technology</option>
										<option value='healthcare'>Healthcare</option>
										<option value='finance'>Finance</option>
										<option value='retail'>Retail</option>
										<option value='education'>Education</option>
										<option value='manufacturing'>Manufacturing</option>
										<option value='other'>Other</option>
									</Select>
								</FormControl>
								<FormControl>
									<FormLabel>Timezone</FormLabel>
									<Select
										value={settings.timezone}
										onChange={(e) => updateSetting('timezone', e.target.value)}>
										<option value='utc-8'>Pacific Time (UTC-8)</option>
										<option value='utc-7'>Mountain Time (UTC-7)</option>
										<option value='utc-6'>Central Time (UTC-6)</option>
										<option value='utc-5'>Eastern Time (UTC-5)</option>
										<option value='utc+0'>GMT (UTC+0)</option>
										<option value='utc+1'>CET (UTC+1)</option>
									</Select>
								</FormControl>
							</VStack>
						</Box>

						<Divider />

						{/* Working Hours */}
						<Box>
							<Heading size='md' mb={4}>
								Working Hours
							</Heading>
							<HStack spacing={4}>
								<FormControl>
									<FormLabel>Start Time</FormLabel>
									<Input
										type='time'
										value={settings.workingHours.start}
										onChange={(e) =>
											updateSetting('workingHours.start', e.target.value)
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>End Time</FormLabel>
									<Input
										type='time'
										value={settings.workingHours.end}
										onChange={(e) =>
											updateSetting('workingHours.end', e.target.value)
										}
									/>
								</FormControl>
							</HStack>
						</Box>

						<Divider />

						{/* Email Signature */}
						<Box>
							<Heading size='md' mb={4}>
								Email Signature
							</Heading>
							<FormControl>
								<FormLabel>Default Email Signature</FormLabel>
								<Textarea
									value={settings.emailSignature}
									onChange={(e) =>
										updateSetting('emailSignature', e.target.value)
									}
									placeholder='Enter your default email signature...'
									rows={4}
								/>
							</FormControl>
						</Box>

						<Divider />

						{/* Notifications */}
						<Box>
							<Heading size='md' mb={4}>
								Notifications
							</Heading>
							<VStack spacing={4} align='stretch'>
								<FormControl display='flex' alignItems='center'>
									<FormLabel htmlFor='email-campaigns' mb='0'>
										Email Campaign Notifications
									</FormLabel>
									<Switch
										id='email-campaigns'
										isChecked={settings.notifications.emailCampaigns}
										onChange={(e) =>
											updateSetting(
												'notifications.emailCampaigns',
												e.target.checked,
											)
										}
									/>
								</FormControl>
								<FormControl display='flex' alignItems='center'>
									<FormLabel htmlFor='system-alerts' mb='0'>
										System Alerts
									</FormLabel>
									<Switch
										id='system-alerts'
										isChecked={settings.notifications.systemAlerts}
										onChange={(e) =>
											updateSetting(
												'notifications.systemAlerts',
												e.target.checked,
											)
										}
									/>
								</FormControl>
								<FormControl display='flex' alignItems='center'>
									<FormLabel htmlFor='team-activity' mb='0'>
										Team Activity Notifications
									</FormLabel>
									<Switch
										id='team-activity'
										isChecked={settings.notifications.teamActivity}
										onChange={(e) =>
											updateSetting(
												'notifications.teamActivity',
												e.target.checked,
											)
										}
									/>
								</FormControl>
							</VStack>
						</Box>

						<Divider />

						{/* Branding */}
						<Box>
							<Heading size='md' mb={4}>
								Branding
							</Heading>
							<VStack spacing={4} align='stretch'>
								{/* Logo Upload */}
								<FormControl>
									<FormLabel>Company Logo</FormLabel>
									<VStack spacing={3} align='start'>
										{settings.branding.logo && (
											<Box
												border='1px solid'
												borderColor='gray.200'
												borderRadius='md'
												p={2}
												maxW='150px'>
												<Image
													src={settings.branding.logo}
													alt='Company Logo'
													maxH='100px'
													maxW='130px'
													objectFit='contain'
												/>
											</Box>
										)}
										<Box>
											<UploadthingWidget
												uploadEndpoint='companyLogo'
												uploadHeaders={{ 'x-company-id': companyId }}
												accept='image/*'
												onUploadSuccess={(url) => {
													updateSetting('branding.logo', url);
													if (companyId && url) {
														patchCompanyBrandingLogoInCache(
															queryClient,
															companyId,
															url,
														);
														invalidateCompanyBrandingCache(
															queryClient,
															companyId,
														);
													}
													toast({
														title: 'Success',
														description: 'Logo uploaded successfully',
														status: 'success',
														duration: 3000,
													});
												}}
											/>
											<Text fontSize='xs' color='gray.500' mt={1}>
												Max file size: 5MB. Recommended: 300x300px
											</Text>
										</Box>
									</VStack>
								</FormControl>

								{/* Color Pickers */}
								<HStack spacing={4}>
									<FormControl>
										<FormLabel>Sidebar Background Color</FormLabel>
										<Box
											as='input'
											type='color'
											value={settings.branding.sidebarBackgroundColor}
											onChange={(e) =>
												updateSetting(
													'branding.sidebarBackgroundColor',
													e.target.value,
												)
											}
											w='90px'
											h='90px'
											borderRadius='50%'
											border='none'
											outline='none'
											cursor='pointer'
											sx={{
												'&::-webkit-color-swatch-wrapper': {
													padding: 0,
													borderRadius: '50%',
												},
												'&::-webkit-color-swatch': {
													borderRadius: '50%',
													border: 'none',
												},
												'&::-moz-color-swatch': {
													borderRadius: '50%',
													border: 'none',
												},
											}}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Sidebar Text Color</FormLabel>
										<Box
											as='input'
											type='color'
											value={settings.branding.sidebarTextColor}
											onChange={(e) =>
												updateSetting(
													'branding.sidebarTextColor',
													e.target.value,
												)
											}
											w='90px'
											h='90px'
											borderRadius='50%'
											border='none'
											outline='none'
											cursor='pointer'
											sx={{
												'&::-webkit-color-swatch-wrapper': {
													padding: 0,
													borderRadius: '50%',
												},
												'&::-webkit-color-swatch': {
													borderRadius: '50%',
													border: 'none',
												},
												'&::-moz-color-swatch': {
													borderRadius: '50%',
													border: 'none',
												},
											}}
										/>
									</FormControl>
								</HStack>
							</VStack>
						</Box>
					</VStack>
				</ModalBody>
				<ModalFooter>
					<Button variant='ghost' mr={3} onClick={onClose}>
						Cancel
					</Button>
					<Button
						colorScheme='teal'
						onClick={handleSaveSettings}
						isLoading={isSaving}
						loadingText='Saving...'>
						Save Settings
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default SettingsModal;
